import { HvacResults, NativeError, RuntimeError, Uuid } from '@belimo-retrofit-portal/logic';
import { TolgeeProvider } from '@tolgee/react';
import { left, right } from 'fp-ts/Either';
import { pipe } from 'fp-ts/function';
import * as A from 'fp-ts/ReadonlyArray';
import { map, Separated } from 'fp-ts/Separated';
import React from 'react';
import { Provider } from 'react-redux';
import { SagaIterator } from 'redux-saga';
import { call as callEffect, delay as delayEffect } from 'redux-saga/effects';
import { useParametricSelector } from 'src/hooks/useParametricSelector';
import { PdfEmbedding } from 'src/modules/common/types/PdfEmbedding';
import { PdfMetadata } from 'src/modules/common/types/PdfMetadata';
import { Project } from 'src/modules/common/types/Project';
import { getApiBaseUrl } from 'src/modules/config/selectors/getApiBaseUrl';
import { getLanguage } from 'src/modules/config/selectors/getLanguage';
import { getTolgeeInstance } from 'src/modules/config/selectors/getTolgeeInstance';
import { getHvacResults } from 'src/modules/hvac/selectors/getHvacResults';
import { getLoggedInUser } from 'src/modules/login/selectors/getLoggedInUser';
import { OutputDocument } from 'src/modules/output/views/OutputDocument';
import { waitReportDocument } from 'src/modules/report/sagas/utils/waitReportDocument';
import { getReportFakeStore } from 'src/modules/report/selectors/getReportFakeStore';
import { ReportFormData } from 'src/modules/report/types/ReportFormData';
import { ReportGenerated } from 'src/modules/report/types/ReportGenerated';
import { createShadowHost } from 'src/modules/report/utils/createShadowHost';
import { renderComponent } from 'src/modules/report/utils/renderComponent';
import { ReportDocumentContent } from 'src/modules/report/views/ReportDocumentContent';
import { htmlToPdf } from 'src/modules/worker/sagas/utils/htmlToPdf';
import { mergePdf } from 'src/modules/worker/sagas/utils/mergePdf';
import { assertDefined } from 'src/utils/assert';
import { call, race, select } from 'typed-redux-saga';

export function* buildReportDocument(
  document: ReportGenerated,
  formData: ReportFormData,
  project: Project,
  embeddings: Readonly<Record<Uuid, PdfMetadata>>,
  results: HvacResults,
): SagaIterator<Blob> {
  const host = yield* call(createShadowHost);

  try {
    const language = yield* select(getLanguage);
    const store = yield* select(getReportFakeStore);
    const tolgee = yield* select(getTolgeeInstance);
    const loggedInUser = yield* select(getLoggedInUser);

    const content = (
      <Provider store={store}>
        <TolgeeProvider tolgee={tolgee}>
          <OutputDocument embeddings={formData.appendix.enabled ? embeddings : {}}>
            <ReportDocumentContent
              results={results}
              document={document}
              formData={formData}
              project={project}
              language={language}
              userSettings={loggedInUser.settings}
              embeddings={embeddings}
            />
          </OutputDocument>
        </TolgeeProvider>
      </Provider>
    );

    yield* call(renderComponent, host, content);
    const { timeout } = yield* race({
      timeout: delayEffect(5_000),
      complete: callEffect(waitReportDocument, host),
    });
    if (timeout) {
      throw new RuntimeError('Timeout exceeded for document readiness');
    }

    const structure = Array.from(host.element.querySelectorAll('.bp-output-page, .bp-output-embedding'));
    const apiBaseUrl = yield* select(getApiBaseUrl);

    const { left: pages, right: embedded } = yield* call(detectEmbeddings, structure, apiBaseUrl);

    const rendered = yield* call(htmlToPdf, pages);
    return yield* call(mergePdf, rendered, embedded);
  } catch (error) {
    throw new RuntimeError('Could not build report PDF document', {}, NativeError.wrap(error));
  } finally {
    yield* call(host.destroy);
  }
}

function detectEmbeddings(
  pages: ReadonlyArray<Element>,
  apiBaseUrl: string,
): Separated<ReadonlyArray<Element>, ReadonlyArray<PdfEmbedding>> {
  return pipe(
    pages.map((element, index) => {
      if (
        element instanceof HTMLElement &&
        element.classList.contains('bp-output-embedding')
      ) {
        const fileId = assertDefined(
          element.dataset.fileId,
          'Missing file identifier in embedded document',
          { element },
        );

        return right({
          fileUrl: `${apiBaseUrl}/source/${encodeURIComponent(fileId)}`,
          insertAt: index,
        });
      }

      return left(element);
    }),
    A.separate,
    map((embeddings) => embeddings.map(({ fileUrl, insertAt }, index) => ({
      fileUrl: fileUrl,
      insertAt: insertAt - index,
    }))),
  );
}
